import { rank, route, type Finding } from '@/shared/ui/council'
import type { TransferAnalysisResponse } from '@/shared/api/types'
import { INTAKE_CHANNEL_LABEL, TRANSFER_REASON_LABEL } from '../api'

export type { Finding }

const STAGE_LABEL: Record<string, string> = {
  RAISED: 'raised',
  SUBMITTED: 'submitted for checking',
  APPROVED: 'approved',
  LODGED: 'lodged with the IEBC',
  RECORDED: 'recorded on our register',
}

/**
 * The transfer council — voter movement read as decisions.
 *
 * ## What a transfer operation is actually for
 *
 * Moving a supporter's registration to the station where they will be on polling day. So
 * the questions are: is the net movement going our way, where is the pipeline losing
 * people, and will the ones still open finish before the register closes. Volume is not
 * one of them — a thousand requests that never get recorded change no votes at all.
 *
 * ## Three claims this council refuses to make
 *
 * **It never says the platform performed a transfer.** The IEBC does; these count the ones
 * we recorded. A council that spoke as though the register were ours would encourage
 * somebody to promise a voter an outcome nobody here controls.
 *
 * **It never treats an open request as a failure.** Open means in flight. Reporting it as
 * loss makes the success rate look worse than it is and hides the real question, which is
 * whether it will land in time.
 *
 * **It never infers intent from movement.** A station losing registrations is a fact about
 * where people are moving, not evidence that anybody organised it.
 */
export function advise(d: TransferAnalysisResponse): Finding[] {
  const out: Finding[] = []
  const num = (n: number | null | undefined) =>
    n == null ? '—' : Math.round(n).toLocaleString()
  const pct = (n: number | null | undefined) =>
    n == null ? '—' : `${Math.round(n * 100)}%`

  const resolved = d.recorded + d.rejected

  // ── is the movement going our way? ────────────────────────────────────────
  const units = [...(d.byUnit ?? [])].filter((u) => u.recorded > 0 || u.lost > 0)
  if (units.length > 0) {
    const gainers = [...units].sort((a, b) => b.net - a.net).slice(0, 3)
    const losers = [...units].sort((a, b) => a.net - b.net).slice(0, 3)
    const netTotal = units.reduce((n, u) => n + u.net, 0)
    out.push({
      id: 'movement',
      severity: 'watch',
      headline: netTotal === 0
        ? 'Registrations moved, but nowhere gained on balance.'
        : `Net movement is ${netTotal > 0 ? '+' : ''}${num(netTotal)} across the areas in view.`,
      evidence: `Biggest gains: ${gainers.filter((u) => u.net > 0)
        .map((u) => `${u.unitName} +${num(u.net)}`).join(', ') || 'none'}. `
        + `Biggest losses: ${losers.filter((u) => u.net < 0)
          .map((u) => `${u.unitName} ${num(u.net)}`).join(', ') || 'none'}.`,
      action: 'Check the gaining areas against where your vote actually needs to be on the day. Movement into a seat you already win is effort that changed nothing; movement into a marginal one is the whole point of running this operation.',
      votesAtStake: Math.abs(netTotal),
      cellCodes: gainers.filter((u) => u.net !== 0).map((u) => u.unitName),
    })
  }

  // ── where the pipeline loses people ───────────────────────────────────────
  const funnel = d.funnel ?? []
  if (funnel.length >= 2) {
    let worst = { from: funnel[0]!, to: funnel[1]!, lost: funnel[0]!.count - funnel[1]!.count }
    for (let i = 1; i < funnel.length - 1; i += 1) {
      const lost = funnel[i]!.count - funnel[i + 1]!.count
      if (lost > worst.lost) worst = { from: funnel[i]!, to: funnel[i + 1]!, lost }
    }
    if (worst.lost > 0) {
      const share = worst.from.count > 0 ? worst.lost / worst.from.count : 0
      out.push({
        id: 'funnel',
        severity: share > 0.4 ? 'serious' : 'watch',
        headline: `The biggest loss is between ${STAGE_LABEL[worst.from.stage] ?? worst.from.stage}`
          + ` and ${STAGE_LABEL[worst.to.stage] ?? worst.to.stage} — ${num(worst.lost)} requests.`,
        evidence: funnel.map((f) => `${STAGE_LABEL[f.stage] ?? f.stage} ${num(f.count)}`).join(' → ')
          + `. That step loses ${pct(share)} of what reaches it.`,
        action: worst.to.stage === 'SUBMITTED'
          ? 'Requests are being raised and never submitted, which is a paperwork or consent problem on our side and the cheapest one on this page to fix. Find out which gate they are failing before adding more intake.'
          : worst.to.stage === 'RECORDED'
            ? 'Requests are reaching the IEBC and not coming back recorded. That is the step we control least, so it needs chasing by reference rather than more volume at the top.'
            : 'This is the step to work on. Adding intake above a stage that already loses this much just makes the queue longer without changing the number that lands.',
        votesAtStake: worst.lost,
      })
    }
  }

  // ── is the pipeline healthy compared with last time? ──────────────────────
  if (d.successRate != null) {
    const before = d.previous?.successRate ?? null
    const delta = before == null ? null : (d.successRate - before) * 100
    out.push({
      id: 'success',
      severity: d.successRate < 0.5 ? 'serious' : d.successRate < 0.75 ? 'watch' : 'good',
      headline: `${pct(d.successRate)} of resolved requests were recorded`
        + (delta == null ? '.'
          : delta < -5 ? `, down ${Math.abs(Math.round(delta))} points on the previous window.`
          : delta > 5 ? `, up ${Math.round(delta)} points on the previous window.`
          : ', roughly level with the previous window.'),
      evidence: `${num(d.recorded)} recorded and ${num(d.rejected)} rejected out of `
        + `${num(resolved)} resolved. ${num(d.open)} are still open and are not counted either way.`,
      action: d.successRate < 0.75
        ? 'A quarter or more of the work is being wasted. Look at the rejection reasons before recruiting more intake — the cost of a rejected request is the same as a recorded one and it moves nobody.'
        : 'The pipeline is converting. Volume at the top is now the constraint rather than quality, so intake is where more effort pays.',
      votesAtStake: d.rejected,
    })
  }

  // ── will the open ones land in time? ──────────────────────────────────────
  if (d.open > 0) {
    const days = d.medianDaysToRecord
    out.push({
      id: 'open',
      severity: d.open > d.recorded ? 'serious' : 'watch',
      headline: `${num(d.open)} requests are still in flight.`,
      evidence: days == null
        ? 'Nothing has been recorded in this window, so there is no typical time to completion yet.'
        : `Half of what got recorded took ${days.toFixed(1)} days from being raised.`,
      action: days == null
        ? 'Open is not failure — it means in flight. But with nothing recorded yet there is no evidence the pipeline completes at all, and that has to be established before any of these are promised to a voter.'
        // A median under a day is real and common on a small base; "about 0 days" reads as a
        // broken calculation rather than as a fast pipeline.
        : days < 1
          ? 'Recording has been taking under a day, so the closing date is not the constraint yet — but that median is over very few requests, and it will lengthen as volume rises. Re-check it before relying on it.'
          : `At that pace these need about ${Math.ceil(days)} days clear of the register's closing date. Anything raised later than that will not land, and telling a voter otherwise is worse than not raising it.`,
      votesAtStake: d.open,
    })
  }

  // ── how the resolved ones ended ───────────────────────────────────────────
  const outcomes = Object.entries(d.outcomes ?? {}).filter(([, n]) => n > 0)
  const bad = outcomes.filter(([k]) => k === 'REJECTED' || k === 'FAILED_AT_IEBC' || k === 'REVERSED')
  if (bad.length > 0) {
    const total = bad.reduce((n, [, v]) => n + v, 0)
    const atIebc = d.outcomes?.FAILED_AT_IEBC ?? 0
    out.push({
      id: 'outcomes',
      severity: resolved > 0 && total / resolved > 0.3 ? 'serious' : 'watch',
      headline: `${num(total)} requests ended badly`
        + (atIebc > 0 ? `, ${num(atIebc)} of them at the IEBC rather than with us.` : '.'),
      evidence: outcomes.map(([k, v]) => `${k.replace(/_/g, ' ').toLowerCase()} ${num(v)}`).join(' · '),
      action: atIebc > 0
        ? 'A failure at the IEBC is different from one of ours: our checks passed and theirs did not, so the fix is in what we submit rather than in who we accept. Get the reasons on the failed ones and correct the form, not the intake.'
        : 'These failed our own checks, which means they were raised for people who were never going to qualify. That is cheaper to fix at the door than in the queue.',
      votesAtStake: total,
    })
  }

  // ── which stations carry it ───────────────────────────────────────────────
  const stations = [...(d.byStation ?? [])].filter((s) => s.recorded > 0 || s.lost > 0)
  if (stations.length >= 3) {
    const shedding = [...stations].sort((a, b) => a.net - b.net).slice(0, 3).filter((s) => s.net < 0)
    if (shedding.length > 0) {
      out.push({
        id: 'stations',
        severity: 'watch',
        headline: `${shedding.length} stations are losing registrations faster than they gain them.`,
        evidence: shedding.map((s) => `${s.stationName} ${s.net} (gained ${num(s.recorded)}, lost ${num(s.lost)})`).join(' · '),
        action: 'This is a fact about where people are moving, not evidence that anybody organised it. What it changes is your day plan: a station shedding registrations needs fewer agents and fewer vehicles than last cycle, and the ones gaining need more.',
        votesAtStake: Math.abs(shedding.reduce((n, s) => n + s.net, 0)),
        cellCodes: shedding.map((s) => s.stationName),
      })
    }
  }

  // ── where the requests come from ──────────────────────────────────────────
  const channels = Object.entries(d.byChannel ?? {}).filter(([, n]) => n > 0)
  if (channels.length > 0) {
    const total = channels.reduce((n, [, v]) => n + v, 0)
    const top = channels.sort((a, b) => b[1] - a[1])[0]!
    if (top[1] / total > 0.6) {
      out.push({
        id: 'channels',
        severity: 'watch',
        headline: `${pct(top[1] / total)} of requests come in through ${INTAKE_CHANNEL_LABEL[top[0]] ?? top[0]}.`,
        evidence: channels.map(([k, v]) => `${INTAKE_CHANNEL_LABEL[k] ?? k} ${num(v)}`).join(' · '),
        action: 'One channel carrying the operation is a single point of failure — if it stops, intake stops. It also means you are only reaching the people that channel reaches, and the ones it does not are invisible from this page.',
        votesAtStake: 0,
        cellCodes: channels.slice(0, 5).map(([k]) => INTAKE_CHANNEL_LABEL[k] ?? k),
      })
    }
  }

  const reasons = Object.entries(d.byReason ?? {}).filter(([, n]) => n > 0)
  if (reasons.length > 0) {
    const total = reasons.reduce((n, [, v]) => n + v, 0)
    const top = reasons.sort((a, b) => b[1] - a[1])[0]!
    if (top[1] / total > 0.4) {
      out.push({
        id: 'reasons',
        severity: 'watch',
        headline: `${pct(top[1] / total)} of requests give the same reason: ${TRANSFER_REASON_LABEL[top[0]] ?? top[0]}.`,
        evidence: reasons.slice(0, 5)
          .map(([k, v]) => `${TRANSFER_REASON_LABEL[k] ?? k} ${num(v)}`).join(' · '),
        action: 'A dominant reason tells you which argument is actually moving people, which is the one to lead with at the door. It is also worth checking that operators are not simply picking the first option on the form.',
        votesAtStake: 0,
      })
    }
  }

  if (out.length === 0) {
    out.push({
      id: 'quiet',
      severity: 'good',
      headline: 'Nothing in this window crosses a threshold.',
      evidence: `${num(d.raised)} raised, ${num(d.recorded)} recorded, ${num(d.open)} open.`,
      action: 'No action. Widen the range if you are checking whether the operation has been slowing.',
      votesAtStake: 0,
    })
  }

  return rank(out)
}

const ROUTES: { id: string; words: string[] }[] = [
  { id: 'movement', words: ['net', 'gain', 'lose', 'losing', 'moving', 'where', 'swing', 'seat', 'ward', 'county'] },
  { id: 'funnel', words: ['funnel', 'drop', 'lose', 'stage', 'stuck', 'bottleneck', 'why', 'pipeline'] },
  { id: 'success', words: ['success', 'rate', 'working', 'converting', 'better', 'worse', 'how are we'] },
  { id: 'open', words: ['open', 'in flight', 'pending', 'time', 'deadline', 'in time', 'how long', 'backlog'] },
  { id: 'outcomes', words: ['reject', 'fail', 'iebc', 'refused', 'bad', 'outcome', 'reverse'] },
  { id: 'stations', words: ['station', 'polling station', 'agents', 'vans', 'day plan'] },
  { id: 'channels', words: ['channel', 'intake', 'sms', 'ussd', 'field', 'portal', 'where do they come'] },
  { id: 'reasons', words: ['reason', 'why do people', 'motivation', 'argument', 'message'] },
  { id: 'quiet', words: ['quiet', 'anything', 'all clear', 'ok', 'fine'] },
]

export function ask(question: string, findings: Finding[]) {
  return route(question, findings, ROUTES)
}

/**
 * The operation, written out for the model.
 *
 * The three refusals are stated as instructions, because a model handed these counts will
 * otherwise say the platform performs transfers, count open requests as failures, and read
 * a losing station as evidence of organised movement.
 */
export function transferContext(d: TransferAnalysisResponse, findings: Finding[]): string {
  const num = (n: number | null | undefined) =>
    n == null ? '—' : Math.round(n).toLocaleString()
  const map = (r: Record<string, number> | undefined, label: (k: string) => string) =>
    Object.entries(r ?? {}).filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]).map(([k, v]) => `${label(k)} ${v}`).join(', ') || 'none'

  return [
    "You are advising a Kenyan campaign's voter-transfer operation — moving supporters'",
    'registrations to the station where they will actually be on polling day. Answer in plain',
    'language a campaign manager can act on. Three rules you must not break:',
    '(1) The IEBC performs transfers. These figures count the ones WE recorded; never write as',
    'though this platform moved anybody, and never promise a voter an outcome we do not control.',
    '(2) An OPEN request is in flight, not a failure. Do not count it as loss.',
    '(3) A station losing registrations is a fact about where people are moving. It is NOT',
    'evidence that anybody organised it, and you must not imply that it is.',
    '',
    `WINDOW: ${d.from} to ${d.to}${d.scopePath ? ` within ${d.scopePath}` : ''}.`,
    `TOTALS: ${num(d.raised)} raised, ${num(d.recorded)} recorded, ${num(d.rejected)} rejected,`
      + ` ${num(d.open)} still open.`
      + (d.successRate == null ? ' Nothing resolved yet.'
        : ` Success rate ${Math.round(d.successRate * 100)}% of resolved.`)
      + (d.medianDaysToRecord == null ? ''
        : ` Median ${d.medianDaysToRecord.toFixed(1)} days from raised to recorded.`),
    d.previous
      ? `PREVIOUS WINDOW of equal length: ${num(d.previous.raised)} raised, ${num(d.previous.recorded)} recorded`
        + (d.previous.successRate == null ? '.' : `, success ${Math.round(d.previous.successRate * 100)}%.`)
      : '',
    '',
    `PIPELINE: ${(d.funnel ?? []).map((f) => `${STAGE_LABEL[f.stage] ?? f.stage} ${f.count}`).join(' → ')}`,
    `OUTCOMES: ${map(d.outcomes, (k) => k.replace(/_/g, ' ').toLowerCase())}`,
    `REASONS GIVEN: ${map(d.byReason, (k) => TRANSFER_REASON_LABEL[k] ?? k)}`,
    `INTAKE CHANNELS: ${map(d.byChannel, (k) => INTAKE_CHANNEL_LABEL[k] ?? k)}`,
    `KIND: ${map(d.byKind, (k) => k.toLowerCase())}`,
    '',
    'AREAS (name, level, gained, lost, net):',
    ...[...(d.byUnit ?? [])].sort((a, b) => b.net - a.net).slice(0, 10)
      .map((u) => `- ${u.unitName} [${u.level}]: gained ${u.recorded}, lost ${u.lost}, net ${u.net}`),
    '',
    'STATIONS (name, gained, lost, net, still open):',
    ...[...(d.byStation ?? [])].sort((a, b) => b.net - a.net).slice(0, 10)
      .map((s) => `- ${s.stationName}: gained ${s.recorded}, lost ${s.lost}, net ${s.net}, open ${s.open}`),
    '',
    'WHAT OUR OWN RULES ALREADY FLAGGED (agree, refine or disagree):',
    ...findings.map((f) => `- [${f.severity}] ${f.headline} ${f.evidence}`),
  ].filter(Boolean).join('\n')
}
